import { getSubjectLabels } from '@/lib/settings';
import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './auth/use-auth';
import { RequirePermission } from './rbac/RequirePermission';
import { accessFor } from '../lib/rbac/pages';
// Layouts
import DashboardLayout from './layouts/DashboardLayout';
import AnimatedPageOutlet from './layouts/AnimatedPageOutlet';
// Auth Pages
import Login from '../pages/auth/Login';
import PasswortVergessen from '../pages/auth/PasswortVergessen';
import PasswortZuruecksetzen from '../pages/auth/PasswortZuruecksetzen';
import Register from '../pages/auth/Register';
import KiFreigabe from '../pages/auth/KiFreigabe';
// Dashboard Pages
import OfficeDashboard from '../pages/dashboard/office/OfficeDashboard';
import PersonManagement from '../pages/dashboard/office/PersonManagement';
import PersonDetail from '../pages/dashboard/office/PersonDetail';
import SubprofileDetail from '../pages/dashboard/office/SubprofileDetail';
import ScheduleManager from '../pages/dashboard/office/ScheduleManager';
import RoomManagement from '../pages/dashboard/office/RoomManagement';
import TrialLessons from '../pages/dashboard/office/TrialLessons';
import TrialRequests from '../pages/dashboard/office/TrialRequests';
import TodoList from '../pages/dashboard/office/TodoList';
import Kalender from '../pages/dashboard/office/Kalender';
import SearchResults from '../pages/dashboard/office/SearchResults';
import AiAssistantChat from '../pages/dashboard/office/AiAssistantChat';
import AccountSecurity from '../pages/dashboard/office/AccountSecurity';
import PersonListsPage from '../pages/dashboard/office/PersonListsPage';
import MigrationCases from '../pages/dashboard/office/MigrationCases';
// Admin Pages
import AdminDashboard from '../pages/dashboard/admin/AdminDashboard';
import CalendarSettings from '../pages/dashboard/admin/CalendarSettings';
import FeatureSettings from '../pages/dashboard/admin/FeatureSettings';
import BrandingSettings from '../pages/dashboard/admin/BrandingSettings';
import RoomTagsSettings from '../pages/dashboard/admin/RoomTagsSettings';
import TenantManagement from '../pages/dashboard/admin/TenantManagement';
import TenantEditorPage from '../pages/dashboard/admin/TenantEditorPage';
import UserManagement from '../pages/dashboard/admin/UserManagement';
import StandortUserManagement from '../pages/dashboard/admin/StandortUserManagement';
import MasterDataManagement from '../pages/dashboard/admin/MasterDataManagement';
import Reports from '../pages/dashboard/admin/Reports';
import TemplatesHub from '../pages/dashboard/admin/TemplatesHub';
import EmailSettingsPage from '../pages/dashboard/admin/EmailSettings';
import StandortFeesSettings from '../pages/dashboard/admin/accounting/StandortFeesSettings';
import TeacherPayoutRequests from '../pages/dashboard/admin/accounting/TeacherPayoutRequests';
import PaymentsOverview from '../pages/dashboard/accounting/PaymentsOverview';
import PaymentRunsPage from '../pages/dashboard/accounting/PaymentRuns';
import BankStatementsPage from '../pages/dashboard/accounting/BankStatements';
import DatevExportPage from '../pages/dashboard/accounting/DatevExport';
import DunningNoticesPage from '../pages/dashboard/accounting/DunningNotices';
import LiquiditaetsprognosePage from '../pages/dashboard/accounting/Liquiditaetsprognose';
import StorageSettings from '../pages/dashboard/admin/StorageSettings';
import PaymentSettingsPage from '../pages/dashboard/admin/PaymentSettings';
import BillingSettingsPage from '../pages/dashboard/admin/BillingSettings';
import TariffsSettingsPage from '../pages/dashboard/admin/TariffsSettings';
import CustomerPortalNews from '../pages/dashboard/admin/CustomerPortalNews';
import StandortJobsPage from '../pages/dashboard/admin/StandortJobsPage';
import StandortAngebotPage from '../pages/dashboard/admin/StandortAngebotPage';
import StandortJobApplicationsPage from '../pages/dashboard/admin/StandortJobApplicationsPage';
import CommunicationSettings from '../pages/dashboard/admin/CommunicationSettings';
import AiSettingsPage from '../pages/dashboard/admin/AiSettings';
import GoogleCalendarSettings from '../pages/dashboard/admin/GoogleCalendarSettings';
import MarketingSettingsPage from '../pages/dashboard/admin/MarketingSettings';
import MarketingCouponCodesPage from '../pages/dashboard/admin/MarketingCouponCodes';
import PurchasedGiftVouchersPage from '../pages/dashboard/admin/PurchasedGiftVouchers';
import MfaSecuritySettings from '../pages/dashboard/admin/MfaSecuritySettings';
// Testing (Tickets dauerhaft, Testdaten nur Testsystem)
import TestDataPage from '../pages/dashboard/testing/TestDataPage';
import TicketsPage from '../pages/dashboard/testing/TicketsPage';
import TicketDetailPage from '../pages/dashboard/testing/TicketDetailPage';
// Abnahme (docs/abnahme-plan.md)
import AbnahmePage from '../pages/dashboard/abnahme/AbnahmePage';
import StoryDetailPage from '../pages/dashboard/abnahme/StoryDetailPage';
import TestlaeufePage from '../pages/dashboard/abnahme/TestlaeufePage';
import FreigabePage from '../pages/dashboard/abnahme/FreigabePage';
import ManuellerLaufPage from '../pages/dashboard/abnahme/ManuellerLaufPage';
import StatistikPage from '../pages/dashboard/abnahme/StatistikPage';
import AblaeufePage from '../pages/dashboard/abnahme/AblaeufePage';
import ReportPage from '../pages/dashboard/abnahme/ReportPage';
import ChatConversationsPage from '../pages/dashboard/admin/ChatConversations';
import GdprRequestsPage from '../pages/dashboard/admin/GdprRequests';
import ProtokollePage from '../pages/dashboard/admin/ProtokollePage';
import KuendigungenPage from '../pages/dashboard/office/KuendigungenPage';
import LehrerwechselPage from '../pages/dashboard/office/LehrerwechselPage';
import KuendigungsfristenSettings from '../pages/dashboard/admin/KuendigungsfristenSettings';
import AvailabilityGroupsSettings from '../pages/dashboard/admin/AvailabilityGroupsSettings';
import MigrationPage from '../pages/dashboard/admin/Migration';
import CmsOverview from '../pages/dashboard/admin/cms/CmsOverview';
import CmsContentPage from '../pages/dashboard/admin/cms/CmsContentPage';
import CmsMediaLibrary from '../pages/dashboard/admin/cms/CmsMediaLibrary';
import CmsEntityContentPage from '../pages/dashboard/admin/cms/CmsEntityContentPage';
import CmsPartnersPage from '../pages/dashboard/admin/cms/CmsPartnersPage';
import CmsTeamMembersPage from '../pages/dashboard/admin/cms/CmsTeamMembersPage';
import CmsPagesPage from '../pages/dashboard/admin/cms/CmsPagesPage';
import CmsBuilderPage from '../pages/dashboard/admin/cms/CmsBuilderPage';
import CmsLandingpagesPage from '../pages/dashboard/admin/cms/CmsLandingpagesPage';
import CmsDesignPage from '../pages/dashboard/admin/cms/CmsDesignPage';
import CmsRedaktionPage from '../pages/dashboard/admin/cms/CmsRedaktionPage';
import CmsSocialPage from '../pages/dashboard/admin/cms/CmsSocialPage';
import LegacyFachMappingsPage from '../pages/dashboard/admin/LegacyFachMappings';
import LegacyUformMappingsPage from '../pages/dashboard/admin/LegacyUformMappings';
import UiPreview from '../pages/dev/UiPreview';
import KeinZugriff from '../pages/KeinZugriff';
import LeererBereich from '../pages/LeererBereich';
// Public pages
import TeacherChangeOffer from '../pages/public/TeacherChangeOffer';

// Nur Anmeldung prüfen. Was jemand sehen darf, entscheidet je Seite der
// Permission-Guard — nie der Rollenname (RBAC Phase 5).
const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return <div className="flex h-screen w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>;
  }
  if (!isAuthenticated) {
    // Nach dem Login zurück hierher (z. B. Freigabe eines KI-Clients).
    return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}` }} />;
  }
  return <>{children}</>;
};

// Seiten-Guard aus der Seitentabelle (lib/rbac/pages.ts). Eine Route ohne Eintrag
// verlangt eine leere Permission-Menge und ist damit für niemanden erreichbar.
const page = (path: string, element: React.ReactNode) => (
  <RequirePermission anyOf={accessFor(path)}>{element}</RequirePermission>
);

const Layout = () => (
  <RequireAuth>
    <DashboardLayout />
  </RequireAuth>
);

// Kleiner Helper fuer Param-erhaltende Redirects (Tenant-Editor etc.).
const RedirectWithId = ({ to }: { to: (id: string) => string }) => {
  const { id } = useParams();
  return <Navigate to={to(id ?? '')} replace />;
};

const RedirectWithPrefixRewrite = ({ from, to }: { from: string; to: string }) => {
  const { pathname, search } = useLocation();
  const suffix = pathname.startsWith(from) ? pathname.slice(from.length) : '';
  return <Navigate to={`${to}${suffix}${search}`} replace />;
};

const AppRoutes = () => {
  return <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<PasswortVergessen />} />
      <Route path="/reset-password" element={<PasswortZuruecksetzen />} />
      <Route path="/register" element={<Register />} />
      <Route path="/lehrerwechsel" element={<TeacherChangeOffer />} />
      <Route path="/__ui-preview" element={<UiPreview />} />
      <Route path="/kein-zugriff" element={<KeinZugriff />} />
      <Route path="/ki-freigabe" element={<RequireAuth><KiFreigabe /></RequireAuth>} />
      {/* Alte Ziel-Route der Rollen-Guards — führte ins Leere und damit in eine Redirect-Schleife. */}
      <Route path="/unauthorized" element={<Navigate to="/kein-zugriff" replace />} />

      {/* Arbeitsbereiche, Personen, Unterricht, Finanzen */}
      <Route path="/dashboard" element={<Layout />}>
        <Route element={<AnimatedPageOutlet />}>
          <Route index element={page('/dashboard', <OfficeDashboard />)} />
          <Route path="people" element={page('/dashboard/people', <PersonManagement />)} />
          <Route path="migration-cases" element={page('/dashboard/migration-cases', <MigrationCases />)} />
          <Route path="person-lists" element={page('/dashboard/person-lists', <PersonListsPage />)} />
          <Route path="people/:id" element={page('/dashboard/people/:id', <PersonDetail />)} />
          <Route path="people/:id/sub/:subId" element={page('/dashboard/people/:id/sub/:subId', <SubprofileDetail />)} />
          <Route path="schedule" element={page('/dashboard/schedule', <ScheduleManager />)} />
          <Route path="rooms" element={page('/dashboard/rooms', <RoomManagement />)} />
          <Route path="trials" element={page('/dashboard/trials', <TrialLessons />)} />
          <Route path="trial-requests" element={page('/dashboard/trial-requests', <TrialRequests />)} />
          <Route path="kuendigungen" element={page('/dashboard/kuendigungen', <KuendigungenPage />)} />
          <Route path="lehrerwechsel" element={page('/dashboard/lehrerwechsel', <LehrerwechselPage />)} />
          <Route path="todo" element={page('/dashboard/todo', <TodoList />)} />
          <Route path="kalender" element={page('/dashboard/kalender', <Kalender />)} />
          <Route path="search" element={page('/dashboard/search', <SearchResults />)} />
          <Route path="ai-chat" element={page('/dashboard/ai-chat', <AiAssistantChat />)} />
          {/* Eigenes Konto: jede angemeldete Person, unabhängig von Permissions. */}
          <Route path="account-security" element={<AccountSecurity />} />
          <Route path="accounting/payments" element={page('/dashboard/accounting/payments', <PaymentsOverview />)} />
          <Route path="accounting/payment-runs" element={page('/dashboard/accounting/payment-runs', <PaymentRunsPage />)} />
          <Route path="accounting/bank-statements" element={page('/dashboard/accounting/bank-statements', <BankStatementsPage />)} />
          <Route path="accounting/dunning" element={page('/dashboard/accounting/dunning', <DunningNoticesPage />)} />
          <Route path="accounting/datev" element={page('/dashboard/accounting/datev', <DatevExportPage />)} />
          <Route path="accounting/liquiditaet" element={page('/dashboard/accounting/liquiditaet', <LiquiditaetsprognosePage />)} />
          {/* Menüpunkte ohne eigene Seite: leerer Zustand statt Fehler. */}
          <Route path="bereich/:nodeId" element={<LeererBereich />} />
        </Route>
      </Route>

      {/* Mandantweite Konfiguration */}
      <Route path="/system" element={<Layout />}>
        <Route element={<AnimatedPageOutlet />}>
          <Route index element={page('/system', <AdminDashboard />)} />

          {/* Organisation */}
          <Route path="organization/tenants" element={page('/system/organization/tenants', <TenantManagement />)} />
          <Route path="organization/tenants/new" element={page('/system/organization/tenants/new', <TenantEditorPage />)} />
          <Route path="organization/tenants/:id" element={page('/system/organization/tenants/:id', <TenantEditorPage />)} />
          <Route path="organization/users" element={page('/system/organization/users', <UserManagement />)} />
          <Route path="organization/security" element={page('/system/organization/security', <MfaSecuritySettings />)} />
          {/* Testdaten sind unter „Testing" umgezogen; alte Lesezeichen bleiben gültig. */}
          <Route path="testdaten" element={<Navigate to="/testing/testdaten" replace />} />

          {/* CMS – zentrale Steuerung der Website-Inhalte */}
          <Route path="cms" element={page('/system/cms', <CmsOverview />)} />
          <Route path="cms/home" element={page('/system/cms/home', <CmsContentPage slug="home" />)} />
          <Route path="cms/ueber-uns" element={page('/system/cms/ueber-uns', <CmsContentPage slug="ueber-uns" />)} />
          <Route path="cms/navigation" element={page('/system/cms/navigation', <CmsContentPage slug="navbar" />)} />
          <Route path="cms/footer" element={page('/system/cms/footer', <CmsContentPage slug="footer" />)} />
          <Route path="cms/standorte" element={page('/system/cms/standorte', <CmsEntityContentPage kind="standort" />)} />
          <Route path="cms/faecher" element={page('/system/cms/faecher', <CmsEntityContentPage kind="fach" />)} />
          <Route path="cms/faq" element={page('/system/cms/faq', <CmsContentPage slug="faq" title="FAQ" description={`Zentrale FAQs, die pro Landingpage (${getSubjectLabels().plural}, Standort × ${getSubjectLabels().singular}) ein-/ausgeblendet werden können.`} />)} />
          <Route path="cms/jobs" element={page('/system/cms/jobs', <CmsContentPage slug="jobs" title="Jobs-Seite" description="Hero-Texte der übergreifenden Job-Seite (/jobs). Die Stellen selbst werden je Standort gepflegt und dort automatisch zusammengeführt." />)} />
          <Route path="cms/familycard" element={page('/system/cms/familycard', <CmsContentPage slug="familycard" title="FamilyCard-Seite" description="Hero-Texte und Vorteile der übergreifenden FamilyCard-Seite (/familycard). Die Partner werden unter FamilyCard gepflegt." />)} />
          <Route path="cms/news" element={page('/system/cms/news', <CustomerPortalNews />)} />
          <Route path="cms/rechtstexte" element={page('/system/cms/rechtstexte', <CmsContentPage slug="rechtstexte" title="Rechtstexte" description="Impressum, Datenschutzerklärung und AGB der Website." />)} />
          <Route path="cms/cookie" element={page('/system/cms/cookie', <CmsContentPage slug="cookie" title="Cookie-Banner" description="Text und Kategorien des Cookie-Consent-Banners." />)} />
          <Route path="cms/partners" element={page('/system/cms/partners', <CmsPartnersPage />)} />
          <Route path="cms/team" element={page('/system/cms/team', <CmsTeamMembersPage />)} />
          <Route path="cms/seiten" element={page('/system/cms/seiten', <CmsPagesPage />)} />
          <Route path="cms/builder" element={page('/system/cms/builder', <CmsBuilderPage />)} />
          <Route path="cms/landingpages" element={page('/system/cms/landingpages', <CmsLandingpagesPage />)} />
          <Route path="cms/design" element={page('/system/cms/design', <CmsDesignPage />)} />
          <Route path="cms/branding" element={page('/system/cms/branding', <BrandingSettings />)} />
          <Route path="cms/media" element={page('/system/cms/media', <CmsMediaLibrary />)} />
          <Route path="cms/redaktion" element={page('/system/cms/redaktion', <CmsRedaktionPage />)} />
          <Route path="cms/social" element={page('/system/cms/social', <CmsSocialPage />)} />

          {/* Alte Erscheinungsbild-Pfade bleiben nur als Bookmark-kompatible Redirects. */}
          <Route path="appearance/branding" element={<Navigate to="/system/cms/branding" replace />} />
          <Route path="appearance/seo" element={<Navigate to="/system/cms" replace />} />
          <Route path="appearance/wordpress" element={<Navigate to="/system/platform/features" replace />} />
          <Route path="appearance/customer-portal" element={<Navigate to="/system/cms/news" replace />} />

          {/* Finanzen */}
          <Route path="finance/stripe" element={page('/system/finance/stripe', <PaymentSettingsPage />)} />
          <Route path="finance/billing" element={page('/system/finance/billing', <BillingSettingsPage />)} />
          <Route path="finance/accounting-general" element={<Navigate to="/system/finance/stripe" replace />} />
          <Route path="finance/payments" element={<Navigate to="/dashboard/accounting/payments" replace />} />
          <Route path="finance/payouts" element={page('/system/finance/payouts', <TeacherPayoutRequests />)} />

          {/* Kommunikation */}
          <Route path="communication/email" element={page('/system/communication/email', <EmailSettingsPage />)} />
          <Route path="communication/settings" element={page('/system/communication/settings', <CommunicationSettings />)} />
          <Route path="communication/templates" element={page('/system/communication/templates', <TemplatesHub />)} />
          <Route path="communication/chat" element={page('/system/communication/chat', <ChatConversationsPage />)} />

          {/* Plattform */}
          <Route path="platform/features" element={page('/system/platform/features', <FeatureSettings />)} />
          <Route path="platform/calendar" element={page('/system/platform/calendar', <CalendarSettings />)} />
          <Route path="platform/storage" element={page('/system/platform/storage', <StorageSettings />)} />
          <Route path="platform/ai" element={page('/system/platform/ai', <AiSettingsPage />)} />
          <Route path="platform/google-calendar" element={page('/system/platform/google-calendar', <GoogleCalendarSettings />)} />

          {/* Marketing */}
          <Route path="marketing" element={page('/system/marketing', <MarketingSettingsPage />)} />
          <Route path="marketing/gutscheincodes" element={page('/system/marketing/gutscheincodes', <MarketingCouponCodesPage />)} />
          <Route path="marketing/geschenkgutscheine" element={page('/system/marketing/geschenkgutscheine', <PurchasedGiftVouchersPage />)} />

          {/* Compliance */}
          <Route path="compliance/gdpr" element={page('/system/compliance/gdpr', <GdprRequestsPage />)} />
          <Route path="logs" element={page('/system/logs', <ProtokollePage />)} />

          {/* Berichte */}
          <Route path="reports" element={page('/system/reports', <Reports />)} />
        </Route>
      </Route>

      {/* Konfiguration pro Standort */}
      <Route path="/standort" element={<Layout />}>
        <Route element={<AnimatedPageOutlet />}>
          <Route index element={<Navigate to="/standort/master-data" replace />} />
          <Route path="master-data" element={page('/standort/master-data', <MasterDataManagement />)} />
          <Route path="rooms" element={page('/standort/rooms', <RoomTagsSettings />)} />
          <Route path="users" element={page('/standort/users', <StandortUserManagement />)} />
          <Route path="rules/kuendigungsfristen" element={page('/standort/rules/kuendigungsfristen', <KuendigungsfristenSettings />)} />
          <Route path="rules/unterrichtsfreie-zeiten" element={page('/standort/rules/unterrichtsfreie-zeiten', <AvailabilityGroupsSettings />)} />
          <Route path="rules/gebuehren" element={page('/standort/rules/gebuehren', <StandortFeesSettings />)} />
          <Route path="tariffs" element={page('/standort/tariffs', <TariffsSettingsPage />)} />
          <Route path="portal-content" element={page('/standort/portal-content', <CustomerPortalNews />)} />
          <Route path="jobs" element={page('/standort/jobs', <StandortJobsPage />)} />
          <Route path="angebot" element={page('/standort/angebot', <StandortAngebotPage />)} />
          <Route path="bewerbungen" element={page('/standort/bewerbungen', <StandortJobApplicationsPage />)} />
          <Route path="migration" element={page('/standort/migration', <MigrationPage />)} />
          <Route path="migration/fach-mappings" element={page('/standort/migration/fach-mappings', <LegacyFachMappingsPage />)} />
          <Route path="migration/uform-mappings" element={page('/standort/migration/uform-mappings', <LegacyUformMappingsPage />)} />
          <Route path="marketing/geschenkgutscheine" element={page('/standort/marketing/geschenkgutscheine', <PurchasedGiftVouchersPage />)} />
        </Route>
      </Route>

      {/* Testing: Tickets dauerhaft, Testdaten nur Testsystem (docs/testing-tickets-plan.md). */}
      <Route path="/testing" element={<Layout />}>
        <Route element={<AnimatedPageOutlet />}>
          <Route index element={page('/testing', <TicketsPage />)} />
          <Route path="testdaten" element={page('/testing/testdaten', <TestDataPage />)} />
          <Route path=":id" element={page('/testing/:id', <TicketDetailPage />)} />
        </Route>
      </Route>

      {/* Abnahme-Katalog (docs/abnahme-plan.md). */}
      <Route path="/abnahme" element={<Layout />}>
        <Route element={<AnimatedPageOutlet />}>
          <Route index element={page('/abnahme', <AbnahmePage />)} />
          <Route path="stories/:id" element={page('/abnahme/stories/:id', <StoryDetailPage />)} />
          <Route path="testlaeufe" element={page('/abnahme/testlaeufe', <TestlaeufePage />)} />
          <Route path="testlaeufe/:id" element={page('/abnahme/testlaeufe/:id', <ManuellerLaufPage />)} />
          <Route path="freigabe" element={page('/abnahme/freigabe', <FreigabePage />)} />
          <Route path="ablaeufe" element={page('/abnahme/ablaeufe', <AblaeufePage />)} />
          <Route path="statistik" element={page('/abnahme/statistik', <StatistikPage />)} />
          <Route path="report" element={page('/abnahme/report', <ReportPage />)} />
        </Route>
      </Route>

      {/* Kontextroute: /system/dashboard/* (Personen aus Admin-Suche) */}
      <Route path="/system/dashboard" element={<Layout />}>
        <Route element={<AnimatedPageOutlet />}>
          <Route index element={<Navigate to="/system" replace />} />
          <Route path="people" element={page('/system/dashboard/people', <PersonManagement />)} />
          <Route path="person-lists" element={page('/system/dashboard/person-lists', <PersonListsPage />)} />
          <Route path="people/:id" element={page('/system/dashboard/people/:id', <PersonDetail />)} />
          <Route path="people/:id/sub/:subId" element={page('/system/dashboard/people/:id/sub/:subId', <SubprofileDetail />)} />
          <Route path="search" element={page('/system/dashboard/search', <SearchResults />)} />
        </Route>
      </Route>

      {/*
        Redirects von alten /admin/*-Pfaden zu den neuen Routen.
        Die Ziel-Route prüft dann die Permission.
      */}

      <Route path="/admin" element={<Navigate to="/system" replace />} />
      <Route path="/admin/system" element={<Navigate to="/system" replace />} />
      <Route path="/admin/system/*" element={<RedirectWithPrefixRewrite from="/admin/system" to="/system" />} />
      <Route path="/admin/standort" element={<Navigate to="/standort" replace />} />
      <Route path="/admin/standort/*" element={<RedirectWithPrefixRewrite from="/admin/standort" to="/standort" />} />
      <Route path="/admin/dashboard" element={<Navigate to="/system/dashboard" replace />} />
      <Route path="/admin/dashboard/*" element={<RedirectWithPrefixRewrite from="/admin/dashboard" to="/system/dashboard" />} />

      {/* Organisation */}
      <Route path="/admin/tenants" element={<Navigate to="/system/organization/tenants" replace />} />
      <Route path="/admin/tenants/new" element={<Navigate to="/system/organization/tenants/new" replace />} />
      <Route path="/admin/tenants/:id" element={<RedirectWithId to={(id) => `/system/organization/tenants/${id}`} />} />
      <Route path="/admin/users" element={<Navigate to="/system/organization/users" replace />} />
      <Route path="/admin/settings/security" element={<Navigate to="/system/organization/security" replace />} />

      {/* Erscheinungsbild */}
      <Route path="/admin/settings/branding" element={<Navigate to="/system/cms/branding" replace />} />
      <Route path="/admin/settings/seo" element={<Navigate to="/system/cms" replace />} />
      <Route path="/admin/settings/wordpress" element={<Navigate to="/system/platform/features" replace />} />
      <Route path="/admin/settings/customer-portal" element={<Navigate to="/system/cms/news" replace />} />

      {/* Finanzen */}
      <Route path="/admin/settings/payment" element={<Navigate to="/system/finance/stripe" replace />} />
      <Route path="/admin/accounting" element={<Navigate to="/system/finance/stripe" replace />} />
      <Route path="/admin/accounting/payments" element={<Navigate to="/dashboard/accounting/payments" replace />} />
      <Route path="/admin/accounting/payouts" element={<Navigate to="/system/finance/payouts" replace />} />

      {/* Kommunikation */}
      <Route path="/admin/settings/email" element={<Navigate to="/system/communication/email" replace />} />
      <Route path="/admin/settings/communication" element={<Navigate to="/system/communication/settings" replace />} />
      <Route path="/admin/templates" element={<Navigate to="/system/communication/templates" replace />} />
      <Route path="/admin/email-templates" element={<Navigate to="/system/communication/templates?tab=email" replace />} />
      <Route path="/admin/sms-templates" element={<Navigate to="/system/communication/templates?tab=sms" replace />} />
      <Route path="/admin/enrollment-pdf-templates" element={<Navigate to="/system/communication/templates?tab=pdf" replace />} />
      <Route path="/admin/history-templates" element={<Navigate to="/system/communication/templates?tab=history" replace />} />
      <Route path="/admin/settings/chat-conversations" element={<Navigate to="/system/communication/chat" replace />} />

      {/* Plattform */}
      <Route path="/admin/settings/features" element={<Navigate to="/system/platform/features" replace />} />
      <Route path="/admin/settings/calendar" element={<Navigate to="/system/platform/calendar" replace />} />
      <Route path="/admin/settings/storage" element={<Navigate to="/system/platform/storage" replace />} />
      <Route path="/admin/settings/ai" element={<Navigate to="/system/platform/ai" replace />} />

      {/* Marketing */}
      <Route path="/admin/settings/marketing" element={<Navigate to="/system/marketing" replace />} />
      <Route path="/admin/settings/marketing/gutscheincodes" element={<Navigate to="/system/marketing/gutscheincodes" replace />} />

      {/* Compliance & Berichte */}
      <Route path="/admin/settings/gdpr" element={<Navigate to="/system/compliance/gdpr" replace />} />
      <Route path="/admin/reports" element={<Navigate to="/system/reports" replace />} />

      {/* Standort-scoped Altpfade */}
      <Route path="/admin/master-data" element={<Navigate to="/standort/master-data" replace />} />
      <Route path="/admin/settings/rooms" element={<Navigate to="/standort/rooms" replace />} />
      <Route path="/admin/settings/kuendigungsfristen" element={<Navigate to="/standort/rules/kuendigungsfristen" replace />} />
      <Route path="/admin/migration" element={<Navigate to="/standort/migration" replace />} />

      {/* Alt: /admin/settings (Einstellungs-Hub) */}
      <Route path="/admin/settings" element={<Navigate to="/system" replace />} />

      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<Navigate to="/dashboard" />} />
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/dashboard" />} />
    </Routes>;
};
export default AppRoutes;
